import { Stack, Text, Box, Inline, Skeleton } from '@machobear/design-system'

import { ReactElement, ReactNode } from 'react'

export const FormBoxSkeleton = () => <Skeleton.Shape height="58px" />

export const FormBox = ({
  children,
  label,
  description,
  icon,
  secondaryLabel
}: {
  children: any
  label?: string
  description?: any
  icon?: any
  secondaryLabel?: any
}) => {
  return (
    <Box
      paddingY="large"
      paddingX={{ mobile: 'medium', tablet: 'large' }}
      background="white"
      borderRadius={{ mobile: 'none', tablet: 'standard' }}
      role="listitem"
      aria-label={label}
      style={{
        boxShadow: '0px 2px 1px 0px rgba(0, 0, 0, 0.05), 0px 0px 1px 0px rgba(0, 0, 0, 0.25)'
      }}
    >
      <Box background="body">
        <Stack space="xlarge">
          {label && (
            <Stack space="small">
              <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                {icon ? (
                  <Box display="flex" alignItems="center" gap="small">
                    <Text size="large" weight="strong" component="h3">
                      {label}
                    </Text>
                    {icon}
                  </Box>
                ) : (
                  <Text size="large" weight="strong" component="h3">
                    {label}
                  </Text>
                )}
                {secondaryLabel ? <Inline space="none">{secondaryLabel}</Inline> : null}
              </Box>
              {description ? description : null}
            </Stack>
          )}
          {children}
        </Stack>
      </Box>
    </Box>
  )
}

export const FormSection = ({
  id,
  children,
  label,
  description,
  optionText,
  tooltip,
  required
}: {
  id?: string
  children: any
  label?: string
  description?: string
  optionText?: ReactElement | null
  tooltip?: ReactNode
  required?: boolean
}) => {
  return (
    <Stack space="gutter" align="left">
      <Stack space="small">
        <Box
          display="flex"
          justifyContent="spaceBetween"
          alignItems={{ mobile: 'flexStart', tablet: 'center' }}
          flexDirection={{ mobile: 'column', tablet: 'row' }}
        >
          <Inline space="small" align="left" alignY="center">
            <Inline space="none" align="left">
              <Text id={id} size="medium" weight="medium">
                {label}
              </Text>
              {required && (
                <Text size="medium" weight="medium" tone="critical">
                  *
                </Text>
              )}
            </Inline>
            <>{tooltip ? tooltip : null}</>
          </Inline>
          {optionText && (
            <Inline space="none" align="right">
              {optionText}
            </Inline>
          )}
        </Box>
        {description ? (
          <Text tone="secondary" size="small">
            {description}
          </Text>
        ) : null}
      </Stack>
      <Stack space="xsmall" align="left">
        {children}
      </Stack>
    </Stack>
  )
}

export const Counter = ({
  name,
  count,
  max,
  isLoading
}: {
  name: string
  count: number
  max: number
  isLoading: boolean
}) => {
  if (isLoading) return <Skeleton.Shape height={24} width={44} />
  const isOverLimit = count > max

  return (
    <Box
      background={isOverLimit ? 'criticalSoft' : 'neutralLight'}
      padding="xxsmall"
      borderRadius="standard"
      role="status"
      aria-label={`${name} limit: ${count}/${max}`}
    >
      <Text size="xsmall" weight="medium">
        {`${count}/${max}`}
      </Text>
    </Box>
  )
}
